import { getMessaging, getToken, deleteToken, isSupported, onMessage } from "firebase/messaging";
import type { MessagePayload } from "firebase/messaging";
import { firebaseAppOrNull, vapidKey, getFirebaseConfig } from "@/lib/firebase";

const SW_PATH = "/firebase-messaging-sw.js";

/** Window event dispatched when a foreground order-status push arrives. Screens
 * (e.g. TrackingScreen) listen for it to refresh immediately instead of waiting
 * for the next poll. detail: { orderId, orderStatus }. */
export const PUSH_ORDER_EVENT = "tappya:push-order";

/** Window event dispatched when a foreground REVIEW push (order_review_request)
 * arrives — distinct from PUSH_ORDER_EVENT so ordinary order pushes keep their
 * existing behavior untouched. App-level code listens for this to navigate to
 * the review screen. detail: { orderId }. */
export const PUSH_REVIEW_EVENT = "tappya:push-review";

/** data.templateKey value the backend sends on delivered/completed to prompt
 * a review (see backend transactional-push M1.5 T3). */
export const REVIEW_PUSH_TEMPLATE_KEY = "order_review_request";

export type OrderPush = {
  title: string;
  body: string;
  orderId: string | null;
  orderStatus: string | null;
  templateKey: string | null;
  reviewUrl: string | null;
};

/** Normalize an FCM payload into the order-status notification we display +
 * the order context the app reacts to. Returns null for an empty payload. */
export function extractOrderPush(payload: Partial<MessagePayload> | null | undefined): OrderPush | null {
  if (!payload || (!payload.notification && !payload.data)) return null;
  const data = (payload.data ?? {}) as Record<string, string>;
  return {
    title: payload.notification?.title || "Tappya",
    body: payload.notification?.body || "",
    orderId: data.orderId ?? null,
    orderStatus: data.orderStatus ?? null,
    templateKey: data.templateKey ?? null,
    reviewUrl: data.reviewUrl ?? null,
  };
}

/** Whether this push should deep-link to the review screen rather than the
 * ordinary order-detail/tracking flow. Primary signal is data.templateKey;
 * fallback to data.category for forward-compat with other senders. */
export function isReviewPush(payload: Partial<MessagePayload> | null | undefined): boolean {
  const data = (payload?.data ?? {}) as Record<string, string>;
  return data.templateKey === REVIEW_PUSH_TEMPLATE_KEY || data.category === "review";
}

/** Deep-link path for a tapped/received push: the review screen for review
 * pushes, otherwise the ordinary order-detail path (existing behavior). Null
 * when there's no orderId to link to. */
export function deepLinkPathForPush(payload: Partial<MessagePayload> | null | undefined): string | null {
  const data = (payload?.data ?? {}) as Record<string, string>;
  const orderId = data.orderId;
  if (!orderId) return null;
  return isReviewPush(payload) ? `/orders/${orderId}/review` : `/orders/${orderId}`;
}

/** Return a backend-provided review URL only when it is constrained to the
 * production storefront and review-route shape. Push data is untrusted input. */
export function reviewUrlForPush(
  payload: Partial<MessagePayload> | null | undefined,
): string | null {
  if (!isReviewPush(payload)) return null;
  const data = (payload?.data ?? {}) as Record<string, string>;
  if (!data.reviewUrl) return null;
  try {
    const url = new URL(data.reviewUrl);
    if (
      url.protocol !== "https:" ||
      url.hostname !== "menu.tappya.com" ||
      url.port !== "" ||
      url.username !== "" ||
      url.password !== "" ||
      !/\/orders\/[^/]+\/review\/?$/.test(url.pathname)
    ) {
      return null;
    }
    return url.toString();
  } catch {
    return null;
  }
}

export async function pushSupported(): Promise<boolean> {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "Notification" in window &&
    !!vapidKey() &&
    !!firebaseAppOrNull() &&
    (await isSupported().catch(() => false))
  );
}

/** Whether to surface a tappable "enable order notifications" affordance. We
 * only offer when push is supported AND permission is still "default": a prompt
 * must be triggered by a user gesture, so it can't reliably fire as a
 * side-effect of placing an order. "granted" needs no prompt (capture happens
 * silently); "denied" can't be re-prompted from the page. */
export function shouldOfferPushOptIn(state: {
  supported: boolean;
  permission: NotificationPermission;
}): boolean {
  return state.supported && state.permission === "default";
}

async function registerSW(): Promise<ServiceWorkerRegistration | null> {
  try {
    // If a service worker is already registered (by VitePWA), use it.
    // VitePWA imports firebase-messaging-sw.js into its generated sw.js.
    const existing = await navigator.serviceWorker.getRegistration();
    if (existing) {
      return existing;
    }

    // Fallback if VitePWA is disabled (e.g. in dev mode).
    const cfg = getFirebaseConfig();
    const qs = new URLSearchParams({
      apiKey: cfg.apiKey,
      projectId: cfg.projectId,
      messagingSenderId: cfg.messagingSenderId,
      appId: cfg.appId,
    }).toString();
    return await navigator.serviceWorker.register(`${SW_PATH}?${qs}`, { updateViaCache: "none" });
  } catch (e) {
    console.warn("[push] SW registration failed", e);
    return null;
  }
}

/** Request permission + fetch an FCM token. Returns null on denial/unsupported. */
export async function requestPushToken(): Promise<string | null> {
  if (!(await pushSupported())) return null;
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return null;
  const reg = await registerSW();
  if (!reg) return null;
  const app = firebaseAppOrNull();
  const key = vapidKey();
  if (!app || !key) return null;
  try {
    const messaging = getMessaging(app);
    return (await getToken(messaging, { vapidKey: key, serviceWorkerRegistration: reg })) || null;
  } catch (e) {
    console.warn("[push] getToken failed", e);
    return null;
  }
}

/** Handle a single foreground FCM message: surface a notification (FCM does NOT
 * auto-display while the tab is focused) and broadcast the order context so open
 * screens can refresh immediately. Ordinary order pushes keep their existing
 * PUSH_ORDER_EVENT behavior unchanged; a review push (data.templateKey ===
 * "order_review_request") ADDITIONALLY broadcasts PUSH_REVIEW_EVENT so the app
 * shell can deep-link to the review screen. */
function handleForegroundPush(payload: MessagePayload): void {
  const info = extractOrderPush(payload);
  if (!info) return;

  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(PUSH_ORDER_EVENT, {
        detail: { orderId: info.orderId, orderStatus: info.orderStatus },
      }),
    );
    if (isReviewPush(payload)) {
      window.dispatchEvent(
        new CustomEvent(PUSH_REVIEW_EVENT, {
          detail: { orderId: info.orderId, reviewUrl: reviewUrlForPush(payload) },
        }),
      );
    }
  }

  // Show the banner via the SW registration (works for foreground too) when the
  // user has granted permission. Best-effort — never throw into the FCM callback.
  // data carries templateKey too, so a background notificationclick (SW) can
  // route review taps the same way this foreground path does.
  try {
    if (
      typeof Notification !== "undefined" &&
      Notification.permission === "granted" &&
      "serviceWorker" in navigator
    ) {
      void navigator.serviceWorker.ready
        .then((reg) =>
          reg.showNotification(info.title, {
            body: info.body,
            icon: "/pwa-192.png",
            badge: "/favicon-32.png",
            data: {
              orderId: info.orderId,
              orderStatus: info.orderStatus,
              templateKey: info.templateKey,
              reviewUrl: info.reviewUrl,
            },
          }),
        )
        .catch(() => undefined);
    }
  } catch {
    /* notifications unavailable */
  }
}

/** Start listening for foreground FCM messages. Returns an unsubscribe fn (or a
 * no-op when push isn't available). Safe to call once at app startup. */
export function startForegroundPushListener(): () => void {
  const app = firebaseAppOrNull();
  if (!app || typeof window === "undefined") return () => undefined;

  let unsubscribe: () => void = () => undefined;
  void isSupported()
    .then((ok) => {
      if (!ok) return;
      try {
        unsubscribe = onMessage(getMessaging(app), handleForegroundPush);
      } catch (e) {
        console.warn("[push] onMessage wiring failed", e);
      }
    })
    .catch(() => undefined);

  return () => unsubscribe();
}

/** Best-effort local token teardown (the deviceId delete is handled by caller). */
export async function clearPushToken(): Promise<void> {
  const app = firebaseAppOrNull();
  if (!app) return;
  try {
    await deleteToken(getMessaging(app));
  } catch (e) {
    console.warn("[push] deleteToken failed", e);
  }
}
